// Shared utility functions for receivables & partner suspension
// Story 46-8: Emlékeztető & Felfüggesztés UI

/** Reminder escalation level (matches backend PaymentReminderService) */
export type ReminderLevel = 'FIRST' | 'SECOND' | 'FINAL';

/** Reminder level metadata */
export interface ReminderLevelInfo {
  level: ReminderLevel;
  label: string;
  description: string;
  tone: 'polite' | 'firm' | 'warning';
}

/** Suspension eligibility result */
export interface SuspensionEligibility {
  eligible: boolean;
  reason: string;
}

/** Days thresholds matching backend REMINDER_THRESHOLDS */
export const REMINDER_THRESHOLDS = {
  FIRST: 7,
  SECOND: 14,
  FINAL: 30,
} as const;

/** Days threshold matching backend SUSPENSION_THRESHOLD_DAYS */
export const SUSPENSION_THRESHOLD_DAYS = 45;

/**
 * Get the appropriate reminder level for a given number of overdue days.
 * Returns null if days overdue is below the first threshold.
 * Matches backend PaymentReminderService.determineSuggestedLevel logic.
 */
export function getReminderLevel(daysOverdue: number): ReminderLevel | null {
  if (daysOverdue < REMINDER_THRESHOLDS.FIRST) {
    return null;
  }
  if (daysOverdue >= REMINDER_THRESHOLDS.FINAL) {
    return 'FINAL';
  }
  if (daysOverdue >= REMINDER_THRESHOLDS.SECOND) {
    return 'SECOND';
  }
  return 'FIRST';
}

/**
 * Get display info for a reminder level.
 */
export function getReminderLevelInfo(level: ReminderLevel): ReminderLevelInfo {
  switch (level) {
    case 'FIRST':
      return {
        level: 'FIRST',
        label: '1. emlékeztető',
        description: 'Udvarias fizetési emlékeztető',
        tone: 'polite',
      };
    case 'SECOND':
      return {
        level: 'SECOND',
        label: '2. felszólítás',
        description: 'Határozott fizetési felszólítás',
        tone: 'firm',
      };
    case 'FINAL':
      return {
        level: 'FINAL',
        label: 'Végleges felszólítás',
        description: 'Végső fizetési felszólítás — felfüggesztés figyelmeztetéssel',
        tone: 'warning',
      };
  }
}

/**
 * Check if a partner is eligible for suspension based on overdue days.
 * VIP partners require manual review (cannot be auto-suspended).
 * Non-VIP partners can be suspended after SUSPENSION_THRESHOLD_DAYS.
 */
export function checkSuspensionEligibility(
  daysOverdue: number,
  isVip: boolean
): SuspensionEligibility {
  if (isVip) {
    return {
      eligible: false,
      reason: 'VIP partner — manuális döntés szükséges',
    };
  }

  if (daysOverdue < SUSPENSION_THRESHOLD_DAYS) {
    return {
      eligible: false,
      reason: `Csak ${SUSPENSION_THRESHOLD_DAYS} nap késés felett felfüggeszthető. Jelenlegi: ${daysOverdue} nap.`,
    };
  }

  return {
    eligible: true,
    reason: `${daysOverdue} napos késés — felfüggesztés engedélyezett`,
  };
}

/**
 * Get CSS classes for reminder level badges.
 */
export function getReminderBadgeClasses(level: ReminderLevel): string {
  switch (level) {
    case 'FIRST':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'SECOND':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'FINAL':
      return 'bg-red-100 text-red-800 border-red-300';
  }
}

/**
 * Get reminder level from aging bucket's minDays.
 * Used to determine which level badge to show in the receivables view.
 */
export function getReminderLevelForBucket(bucketMinDays: number): ReminderLevel | null {
  return getReminderLevel(bucketMinDays);
}
